import clsx from 'clsx';
import React, { useMemo, useRef, useState, useCallback } from 'react';
import type { TranslationLanguages } from 'stream-chat';
import "./ChatMessage.scss";
import {
  areMessageUIPropsEqual,
  Avatar,
  EditMessageForm,
  ErrorIcon,
  isOnlyEmojis,
  MessageContextValue,
  MessageInput,
  MessageRepliesCountButton,
  MessageTimestamp,
  MessageUIComponentProps,
  renderText as defaultRenderText,
  useComponentContext,
  useMessageContext,
  useTranslationContext,
  ReactionsList,
  useChannelStateContext,
  useChatContext,
} from 'stream-chat-react';

import { PinIndicator } from './PinIndicator';
import { useWorkspaceController } from '../context/WorkspaceController';
import type { StreamChatType } from '../types';
import { SessionManager } from '../../services/SessionManager/SessionManager';
import GGCustomMessage from '../../components/PageComponents/ChatComponents/GGCustomMessage';
import { CustomMessageStatus } from './CustomMessageStatus';
import { ReceivedMessageTimestamp } from './ReceivedMessageTimestamp';
import { useQuotedMessage } from '../context/QuotedMessageContext';
import { QuotedMessageDisplay } from '../components/QuotedMessageDisplay';
import { MessageDropdown } from '../components/MessageDropdown';
import GGDialogueBox, { dialogueType } from '../../components/UtilityComponents/GGDialogueBox/GGDialogueBox';
import { ErrorLogger } from '../../utils/Logger';

type MessageTeamWithContextProps = MessageContextValue<StreamChatType> & {
  isReactionEnabled: boolean;
  messageWrapperRef: React.MutableRefObject<HTMLDivElement | null>;
};

const MessageTeamWithContext = (
  props: MessageTeamWithContextProps,
) => {
  const {
    clearEditingState,
    editing,
    groupStyles,
    handleAction,
    handleOpenThread,
    handleRetry,
    initialMessage,
    isReactionEnabled,
    message,
    messageWrapperRef,
    onMentionsClickMessage,
    onMentionsHoverMessage,
    onUserClick,
    onUserHover,
    renderText = defaultRenderText,
    threadList,
  } = props;

  const { channel } = useChannelStateContext<StreamChatType>();
  const { client } = useChatContext<StreamChatType>();
  const { Attachment } = useComponentContext<StreamChatType>('MessageTeam');
  const { setQuotedMessage } = useQuotedMessage();

  const { t, userLanguage } = useTranslationContext('MessageTeam');

  // Custom reply handler for WhatsApp-style replies
  const handleReply = useCallback(() => {
    setQuotedMessage(message);
    // Focus the message input
    const messageInput = document.querySelector('.str-chat__textarea__textarea') as HTMLTextAreaElement;
    if (messageInput) {
      messageInput.focus();
    }
  }, [message, setQuotedMessage]);

  // Delete confirmation dialog state
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  
  // Touch/swipe handling for reply gesture
  const touchStartX = useRef<number>(0);
  const touchStartY = useRef<number>(0);
  const [swipeOffset, setSwipeOffset] = useState(0);
  const messageRef = useRef<HTMLDivElement | null>(null);

  // Delete message handler
  const handleDelete = async () => {
    setShowDeleteDialog(false);
    
    if (!message.id) return;
    
    try {
      // Use Stream Chat client to delete the message
      await client.deleteMessage(message.id);
    } catch (error) {
      ErrorLogger.log(error, 'ChatMessage.handleDelete');
      alert('Failed to delete message. Please try again.');
    }
  };

  // Check if current user can delete this message
  const canDelete = message.user?.id === SessionManager.shared.buyer?.id;
  const isMyMessage = message.user?.id === SessionManager.shared.buyer?.id;

  // Touch handlers for swipe-to-reply
  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    if (message.type === 'system' || message.type === 'error' || initialMessage) return;
    
    const touch = e.touches[0];
    touchStartX.current = touch.clientX;
    touchStartY.current = touch.clientY;
  }, [message.type, initialMessage]);

  const handleTouchMove = useCallback((e: React.TouchEvent) => {
    if (message.type === 'system' || message.type === 'error' || initialMessage) return;

    const touch = e.touches[0];
    const deltaX = touch.clientX - touchStartX.current;
    const deltaY = touch.clientY - touchStartY.current;
    
    // Only track horizontal swipe if it's more horizontal than vertical
    if (Math.abs(deltaX) > Math.abs(deltaY) && Math.abs(deltaX) > 10) {
      e.preventDefault();
      
      // Only allow swipe right (reply gesture)
      if (deltaX > 0 && deltaX < 120) {
        setSwipeOffset(deltaX);
      }
    }
  }, [message.type, initialMessage]);

  const handleTouchEnd = useCallback(() => {
    if (message.type === 'system' || message.type === 'error' || initialMessage) return;

    // If swiped enough (>60px), trigger reply
    if (swipeOffset > 60) {
      handleReply();
      // Haptic feedback
      if (navigator.vibrate) {
        navigator.vibrate(30);
      }
    }
    
    // Reset swipe
    setSwipeOffset(0);
  }, [swipeOffset, handleReply, message.type, initialMessage]);

  const messageTextToRender =
    message.i18n?.[`${userLanguage}_text` as `${TranslationLanguages}_text`] || message.text;

  const messageMentionedUsersItem = message.mentioned_users;

  const messageText = useMemo(() => renderText(messageTextToRender, messageMentionedUsersItem), [
    messageMentionedUsersItem,
    messageTextToRender,
    renderText,
  ]);

  const [messageCopy,] = useState<any>(message);
  const firstGroupStyle = groupStyles ? groupStyles[0] : 'single';

  // Custom deleted message that respects alignment
  if (message.deleted_at) {
    const isMyMessage = message.user?.id === SessionManager.shared.buyer?.id;
    return (
      <div className={clsx('str-chat__message-deleted-wrapper', {
        'str-chat__message-deleted-wrapper--mine': isMyMessage,
        'str-chat__message-deleted-wrapper--theirs': !isMyMessage
      })}>
        <div className='str-chat__message-deleted'>
          <span className='str-chat__message-deleted-text'>
            {isMyMessage ? 'You deleted this message' : 'This message was deleted'}
          </span>
        </div>
      </div>
    );
  }

  if (editing) {
    return (
      <div
        className={`str-chat__message-team str-chat__message-team--${firstGroupStyle} str-chat__message-team--editing`}
        data-testid='message-team-edit'
      >
        {(firstGroupStyle === 'top' || firstGroupStyle === 'single') && (
          <div className='str-chat__message-team-meta'>
            <Avatar
              image={message.user?.image}
              name={message.user?.name || message.user?.id}
              onClick={onUserClick}
              onMouseOver={onUserHover}
              size={34}
            />
          </div>
        )}
        <MessageInput
          clearEditingState={clearEditingState}
          Input={EditMessageForm}
          message={message}
        />
      </div>
    );
  }
  const rootClass = clsx(
    'str-chat__message',
    'str-chat__message-team',
    `str-chat__message-team--${firstGroupStyle}`,
    {
      'pinned-message': message.pinned,
      [`str-chat__message-team--${message.status}`]: message.status,
      [`str-chat__message-team--${message.type}`]: message.type,
      'str-chat__message--has-attachment': !!message.attachments?.length,
      'threadList': threadList,
    },
  );

  return (
    <>
      {message.pinned && <PinIndicator message={message} />}
      <div
        className={rootClass}
        data-testid='message-team'
        data-message-id={message.id}
        ref={messageWrapperRef}
      >
        <div className='avatar-host'>
          {firstGroupStyle === 'top' || firstGroupStyle === 'single' || initialMessage ? (
            <Avatar
              image={message.user?.image}
              name={message.user?.name || message.user?.id}
              onClick={onUserClick}
              onMouseOver={onUserHover}
              size={34}
            />
          ) : (
            <div data-testid='team-meta-spacer' style={{ marginRight: 0, width: 34 }} />
          )}
        </div>
        <div className='str-chat__message-team-group'>
          {(firstGroupStyle === 'top' || firstGroupStyle === 'single' || initialMessage) && (
            <div className='str-chat__message-team-meta'>
              <div
                className='str-chat__message-team-author'
                data-testid='message-team-author'
                onClick={onUserClick}
              >
                <strong>{message.user?.name || message.user?.id}</strong>
                {message.type === 'error' && (
                  <div className='str-chat__message-team-error-header'>
                    {t<string>('Only visible to you')}
                  </div>
                )}
              </div>
              <MessageTimestamp />
            </div>
          )}
          <div
            className={clsx(
              message.user?.id === SessionManager.shared.buyer?.id ? 'str-chat__message-team-content-right' : 'str-chat__message-team-content-left',
              `str-chat__message-team-content--${firstGroupStyle}`,
              `str-chat__message-team-content--${message.text === '' ? 'image' : 'text'}`,
              { 'message-swiping': swipeOffset > 0 }
            )}
            data-testid='message-team-content'
            style={{ transform: `translateX(${swipeOffset}px)`, transition: swipeOffset > 0 ? 'none' : 'transform 0.2s ease' }}
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
            ref={messageRef}
          >
            {/* Reply icon that appears when swiping */}
            {swipeOffset > 0 && (
              <div 
                className="swipe-reply-icon"
                style={{ 
                  opacity: Math.min(swipeOffset / 60, 1),
                  transform: `scale(${Math.min(swipeOffset / 60, 1)})`
                }}
              >
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M10 9V5L3 12L10 19V14.9C15 14.9 18.5 16.5 21 20C20 15 17 10 10 9Z" fill="currentColor"/>
                </svg>
              </div>
            )}
            {/* Old Stream Chat actions - commented out for WhatsApp-style dropdown */}
            {/* {!initialMessage &&
              message.status !== 'sending' &&
              message.status !== 'failed' &&
              message.type !== 'system' &&
              message.type !== 'ephemeral' &&
              message.type !== 'error' && (
                <div
                  className={`str-chat__message-team-actions`}
                  data-testid='message-team-actions'
                >
                  {showDetailedReactions && <ReactionSelector ref={reactionSelectorRef} />}
                  {isReactionEnabled && (
                    <span
                      data-testid='message-team-reaction-icon'
                      onClick={onReactionListClick}
                      title='Reactions'
                    >
                      <ReactionIcon />
                    </span>
                  )}
                  {shouldShowReplies && (
                    <span
                      data-testid='message-team-thread-icon'
                      onClick={handleReply}
                      title='Reply'
                    >
                      <ThreadIcon />
                    </span>
                  )}
                  {showActionsBox && (
                    <MessageActions inline messageWrapperRef={messageWrapperRef} />
                  )}
                </div>
              )} */}
            
            {/* Delete confirmation dialog */}
            <GGDialogueBox 
              type={dialogueType.Alert}
              isOpen={showDeleteDialog}
              message="Are you sure you want to delete this message? This action cannot be undone."
              closeHandler={() => setShowDeleteDialog(false)}
              successHandler={handleDelete}
            />
            {
              (messageCopy && messageCopy.ggCustomMessageData) ? 
                <GGCustomMessage customMessageDetails={messageCopy.ggCustomMessageData} channelId={channel.id ?? ""}/> : message.text && message.text.length !== 0 && 
                <div
                  className={clsx('str-chat__message-team-text', { 'str-chat__message-team-text--is-emoji': isOnlyEmojis(message.text) })}
                  data-testid='message-team-message'
                  onClick={onMentionsClickMessage}
                  onMouseOver={onMentionsHoverMessage}
                >
                  {/* WhatsApp-style dropdown - simple Reply & Delete */}
                  {!initialMessage &&
                    message.status !== 'sending' &&
                    message.status !== 'failed' &&
                    message.type !== 'system' &&
                    message.type !== 'ephemeral' &&
                    message.type !== 'error' && (
                      <div className="str-chat__message-team-actions">
                        <MessageDropdown 
                          onReply={handleReply}
                          onDelete={() => setShowDeleteDialog(true)}
                          canDelete={canDelete}
                          isMyMessage={isMyMessage}
                        />
                      </div>
                    )}
                  {message.quoted_message && <QuotedMessageDisplay quotedMessage={message.quoted_message} />}
                  {messageText}
                  {/* Timestamp for received messages - inside the bubble */}
                  {!isMyMessage && <ReceivedMessageTimestamp />}
                </div>
            }
            {!message.text && message.attachments?.length ? (
              <div className={message.user?.id === SessionManager.shared.buyer?.id ? `str-chat__message-team-content-right` : 'str-chat__message-team-content-left'}>
                <Attachment actionHandler={handleAction} attachments={message.attachments} />
              </div>
            ) : null}
            {message.latest_reactions?.length !== 0 && message.text !== '' && isReactionEnabled && (
              <ReactionsList />
            )}
            {message.status === 'failed' && (
              <button
                className='str-chat__message-team-failed'
                data-testid='message-team-failed'
                onClick={message.errorStatusCode !== 403 ? () => handleRetry(message) : undefined}
              >
                <ErrorIcon />
                {message.errorStatusCode !== 403
                  ? t<string>('Message Failed · Click to try again')
                  : t<string>('Message Failed · Unauthorized')}
              </button>
            )}
          </div>
          <CustomMessageStatus />
          {message.text && message.attachments?.length ? (
            <div className={message.user?.id === SessionManager.shared.buyer?.id ? `str-chat__message-team-content-right` : 'str-chat__message-team-content-left'}>
              <Attachment actionHandler={handleAction} attachments={message.attachments} />
            </div>
          ) : null}
          {message.latest_reactions &&
            message.latest_reactions.length !== 0 &&
            message.text === '' &&
            isReactionEnabled && <ReactionsList />}
          {!threadList && (
            <MessageRepliesCountButton
              onClick={handleOpenThread}
              reply_count={message.reply_count}
            />
          )}
        </div>
      </div>
    </>
  );
};

const MemoizedMessageTeam = React.memo(
  MessageTeamWithContext,
  areMessageUIPropsEqual,
) as typeof MessageTeamWithContext;


export const ChatMessage = (
  props: MessageUIComponentProps<StreamChatType>,
) => {
  const messageContext = useMessageContext<StreamChatType>('MessageTeam');
  const { closePinnedMessageListOpen } = useWorkspaceController();

  const messageWrapperRef = useRef<HTMLDivElement | null>(null);

  const message = props.message || messageContext.message;

  // Check if reactions are enabled (simplified)
  const isReactionEnabled = true; // You can add proper logic here if needed

  const handleOpenThreadOverride = (event: React.BaseSyntheticEvent) => {
    closePinnedMessageListOpen();
    messageContext.handleOpenThread(event);
  };

  return (
    <div className={message.pinned ? 'pinned-message' : 'unpinned-message'}>
      <MemoizedMessageTeam
        {...messageContext}
        isReactionEnabled={isReactionEnabled}
        messageWrapperRef={messageWrapperRef}
        handleOpenThread={handleOpenThreadOverride}
        {...props}
      />
    </div>
  );
};
